import { automationExerciseDetailedCases } from "./automationExerciseDetailedCases";

const buildAuthenticationSteps = ({
  homePath,
  navigationPath,
  precondition,
  action,
  testData,
  assertion,
  negativeCheck,
  title,
}) => [
  `Open ${homePath} and confirm the public landing page is stable before starting ${title.toLowerCase()}.`,
  `Navigate specifically to ${navigationPath} and wait until the auth form is fully interactive.`,
  `Set up the auth state required for this run: ${precondition}.`,
  `Prepare user credentials and profile values for this case: ${testData}.`,
  "Clear old browser session artifacts if the scenario depends on a fresh state.",
  `Execute the authentication activity carefully: ${action}.`,
  "Observe field-level validation, button state, and navigation response after submission.",
  `Assert the main authentication outcome: ${assertion}.`,
  `Run the auth-focused negative or security validation: ${negativeCheck}.`,
  "Capture final route, toast/error content, and session behavior for automation evidence.",
];

const buildCatalogSteps = ({
  navigationPath,
  precondition,
  action,
  testData,
  assertion,
  negativeCheck,
  feature,
}) => [
  `Open the storefront flow at ${navigationPath} for the ${feature.toLowerCase()} validation.`,
  "Wait for listing skeletons, cards, and above-the-fold merchandising content to render cleanly.",
  `Prepare catalog state and data expectation: ${precondition}.`,
  `Use catalog input for this run: ${testData}.`,
  "Count visible product cards before interaction so result changes can be measured accurately.",
  `Perform the listing interaction: ${action}.`,
  "Track whether card order, card count, image rendering, and navigation affordances update correctly.",
  `Confirm the core catalog expectation: ${assertion}.`,
  `Perform the browse or discovery edge-case validation: ${negativeCheck}.`,
  "Capture result count, first card details, and any layout regression evidence for the report.",
];

const buildCheckoutSteps = ({
  navigationPath,
  precondition,
  action,
  testData,
  assertion,
  negativeCheck,
  title,
}) => [
  `Launch the ecommerce flow from ${navigationPath} for ${title.toLowerCase()}.`,
  "Ensure at least one valid product, address path, and purchasable checkout state are available.",
  `Prepare checkout precondition: ${precondition}.`,
  `Use these transactional inputs: ${testData}.`,
  "Record initial cart quantity, subtotal, and address/payment state before changing anything.",
  `Execute the revenue-impacting action: ${action}.`,
  "Observe cart totals, navigation transitions, loader states, and payment/order confirmation behavior.",
  `Validate the expected commerce result: ${assertion}.`,
  `Execute the order-integrity or payment-safety check: ${negativeCheck}.`,
  "Save invoice/order identifiers, screenshots, and summary values for framework assertions.",
];

const buildApiSteps = ({
  precondition,
  action,
  testData,
  assertion,
  negativeCheck,
  feature,
}) => [
  `Identify the ${feature} endpoint, method, and auth requirement for this scenario.`,
  `Set up API precondition and environment data: ${precondition}.`,
  `Prepare request payload, token, and validation set: ${testData}.`,
  "Trigger the API from Postman, Rest Assured, Playwright API context, or your chosen client.",
  `Run the intended contract action: ${action}.`,
  "Inspect status code, response body, headers, schema, and latency from the response.",
  "Cross-check whether UI-visible business behavior matches the backend result where relevant.",
  `Validate the expected API contract result: ${assertion}.`,
  `Run the negative, unauthorized, or malformed-request check: ${negativeCheck}.`,
  "Log request payload, response body, and reproducible evidence for defect or framework reporting.",
];

const buildBugWorkflowSteps = ({
  navigationPath,
  precondition,
  action,
  testData,
  assertion,
  negativeCheck,
  title,
}) => [
  `Open ${navigationPath} for the bug management scenario: ${title}.`,
  "Review the current tracker view and confirm the relevant bug form, list, or detail page is available.",
  `Establish the reporting or tracking context: ${precondition}.`,
  `Collect the evidence and bug data needed: ${testData}.`,
  "Verify the issue is reproducible before logging or updating the ticket.",
  `Perform the defect workflow action: ${action}.`,
  "Check assignee, reporter, status, evidence, and comments after the workflow event.",
  `Confirm the expected tracker outcome: ${assertion}.`,
  `Run the workflow-quality or regression check: ${negativeCheck}.`,
  "Capture final bug state, identifiers, and audit trail details for documentation.",
];

const buildFrameworkSteps = ({
  precondition,
  action,
  testData,
  assertion,
  negativeCheck,
  title,
}) => [
  `Review the framework design target for ${title.toLowerCase()}.`,
  `Establish the engineering context first: ${precondition}.`,
  `List the framework assets involved in this task: ${testData}.`,
  "Identify duplication, flakiness risk, and maintainability pain points in the current approach.",
  `Design or refactor the reusable automation capability: ${action}.`,
  "Map how the component will be consumed by UI, API, smoke, and regression suites.",
  "Check naming conventions, abstraction quality, and config separation while designing.",
  `Verify the maintainability goal is achieved: ${assertion}.`,
  `Challenge the design with a reliability check: ${negativeCheck}.`,
  "Document the final framework pattern so learners can reproduce it in their own project.",
];

const buildExecutionSteps = (config) => {
  switch (config.suite) {
    case "Authentication":
      return buildAuthenticationSteps(config);
    case "Catalog and Discovery":
      return buildCatalogSteps(config);
    case "Cart and Checkout":
      return buildCheckoutSteps(config);
    case "API and Security":
      return buildApiSteps(config);
    case "Bug Reporting and Regression":
      return buildBugWorkflowSteps(config);
    case "Framework Design":
      return buildFrameworkSteps(config);
    default:
      return [
        `Open ${config.homePath}.`,
        `Navigate to ${config.navigationPath}.`,
        `Prepare context: ${config.precondition}.`,
        `Use data: ${config.testData}.`,
        `Execute action: ${config.action}.`,
        "Wait for stable state.",
        `Assert result: ${config.assertion}.`,
        `Check edge case: ${config.negativeCheck}.`,
        "Collect evidence.",
        "Report status.",
      ];
  }
};

const buildScenario = ({
  id,
  suite,
  title,
  difficulty,
  type,
  feature,
  homePath,
  navigationPath,
  precondition,
  action,
  testData,
  assertion,
  negativeCheck,
  problemStatement,
  locators,
}) => ({
  id,
  suite,
  title,
  difficulty,
  type,
  feature,
  problemStatement,
  precondition,
  executionSteps: buildExecutionSteps({
    suite,
    title,
    feature,
    homePath,
    navigationPath,
    precondition,
    action,
    testData,
    assertion,
    negativeCheck,
  }),
  expectedResults: [
    assertion,
    "No broken UI or unexpected duplicate request should appear.",
    "Logs and page state should support clean automation assertions.",
  ],
  locatorHints: locators,
  seleniumHint: `Use explicit waits around ${feature.toLowerCase()} and assert stable state after action.`,
  playwrightHint: `Prefer role or data-testid locators for ${feature.toLowerCase()} and verify network-safe end state.`,
});

const authScenarios = [
  buildScenario({
    id: "auth-01",
    suite: "Authentication",
    title: "Register user with valid mandatory details",
    difficulty: "Intermediate",
    type: "UI E2E",
    feature: "Signup",
    homePath: "/home",
    navigationPath: "/register",
    precondition: "user should not already exist",
    action: "fill registration form and submit valid data",
    testData: "fresh name, unique email, valid password, phone number",
    assertion: "account should be created and success state should be visible",
    negativeCheck: "verify duplicate refresh does not create another account",
    problemStatement: "Fresh users complain that signup works inconsistently when they submit the form quickly.",
    locators: ["input[name='name']", "input[name='email']", "input[name='password']", "button[type='submit']"],
  }),
  buildScenario({
    id: "auth-02",
    suite: "Authentication",
    title: "Block duplicate email registration",
    difficulty: "Intermediate",
    type: "Negative UI",
    feature: "Signup validation",
    homePath: "/home",
    navigationPath: "/register",
    precondition: "user email should already exist",
    action: "attempt signup using an existing email address",
    testData: "existing email with new password and same name",
    assertion: "duplicate email validation should appear clearly",
    negativeCheck: "verify no account is created in success state",
    problemStatement: "Business wants to stop duplicate user records before they reach CRM or learner dashboard.",
    locators: ["input[name='email']", ".error-message", "button[type='submit']"],
  }),
  buildScenario({
    id: "auth-03",
    suite: "Authentication",
    title: "Login with valid credentials",
    difficulty: "Intermediate",
    type: "UI E2E",
    feature: "Login",
    homePath: "/home",
    navigationPath: "/login",
    precondition: "user account should already exist and be active",
    action: "enter valid email and password then click login",
    testData: "registered learner credentials",
    assertion: "user should reach authenticated state or dashboard",
    negativeCheck: "verify reloading the page keeps valid session state",
    problemStatement: "Users say login succeeds but post-login UI does not always reflect authenticated state.",
    locators: ["input[type='email']", "input[type='password']", "button[type='submit']"],
  }),
  buildScenario({
    id: "auth-04",
    suite: "Authentication",
    title: "Show error for invalid password",
    difficulty: "Beginner",
    type: "Negative UI",
    feature: "Login validation",
    homePath: "/home",
    navigationPath: "/login",
    precondition: "user account should exist",
    action: "login using correct email and wrong password",
    testData: "valid email, invalid password",
    assertion: "secure invalid credential message should be shown",
    negativeCheck: "verify no dashboard access is granted",
    problemStatement: "The app should fail gracefully and securely when wrong passwords are used.",
    locators: ["input[type='email']", "input[type='password']", ".error-message"],
  }),
  buildScenario({
    id: "auth-05",
    suite: "Authentication",
    title: "Required field validation on login",
    difficulty: "Beginner",
    type: "Validation",
    feature: "Login form",
    homePath: "/home",
    navigationPath: "/login",
    precondition: "login page should be loaded",
    action: "click login without entering mandatory fields",
    testData: "blank email and blank password",
    assertion: "field-level required validation should appear",
    negativeCheck: "verify no API-style success state is triggered",
    problemStatement: "Frontend validation should block obviously incomplete requests.",
    locators: ["button[type='submit']", "[aria-invalid='true']", ".validation-text"],
  }),
  buildScenario({
    id: "auth-06",
    suite: "Authentication",
    title: "Password strength behavior on registration",
    difficulty: "Intermediate",
    type: "Validation",
    feature: "Password policy",
    homePath: "/home",
    navigationPath: "/register",
    precondition: "registration form should be available",
    action: "enter weak password and attempt account creation",
    testData: "weak password like 12345 or password",
    assertion: "password policy feedback should block weak submission",
    negativeCheck: "verify strong password removes the same validation",
    problemStatement: "The product should teach users strong password rules and prevent weak account creation.",
    locators: ["input[name='password']", ".password-strength", ".validation-text"],
  }),
  buildScenario({
    id: "auth-07",
    suite: "Authentication",
    title: "Logout and session clear verification",
    difficulty: "Intermediate",
    type: "State management",
    feature: "Logout",
    homePath: "/home",
    navigationPath: "/login",
    precondition: "user should already be logged in",
    action: "click logout from authenticated navigation",
    testData: "active logged-in user session",
    assertion: "user should be redirected to login and secure pages should be inaccessible",
    negativeCheck: "verify browser back button does not reopen dashboard state",
    problemStatement: "Logout must remove access cleanly, especially on shared systems.",
    locators: [".logout-action-btn", ".dashboard-action-link", "nav"],
  }),
  buildScenario({
    id: "auth-08",
    suite: "Authentication",
    title: "Role-based dashboard redirect",
    difficulty: "Advanced",
    type: "Access control",
    feature: "Role navigation",
    homePath: "/home",
    navigationPath: "/login",
    precondition: "multiple roles like admin, trainer, student should exist",
    action: "login with each role and observe destination route",
    testData: "role-based valid credentials",
    assertion: "each role should land on the correct dashboard route",
    negativeCheck: "verify student cannot directly browse admin route",
    problemStatement: "Wrong role redirects can become both usability and authorization defects.",
    locators: [".dashboard-action-link", ".crm-navbar-link", ".bug-navbar-link"],
  }),
  buildScenario({
    id: "auth-09",
    suite: "Authentication",
    title: "Unauthenticated access to protected route",
    difficulty: "Advanced",
    type: "Security",
    feature: "Protected routes",
    homePath: "/home",
    navigationPath: "/student",
    precondition: "browser should not have active session",
    action: "open protected route directly in a new tab",
    testData: "no session token or local user state",
    assertion: "unauthenticated user should be redirected to login or unauthorized state",
    negativeCheck: "verify no protected content briefly flashes before redirect",
    problemStatement: "Protected routes should never leak dashboard content to unauthenticated users.",
    locators: ["main", ".login-form", ".unauthorized-page"],
  }),
  buildScenario({
    id: "auth-10",
    suite: "Authentication",
    title: "Form state persistence after validation error",
    difficulty: "Intermediate",
    type: "UX validation",
    feature: "Signup form state",
    homePath: "/home",
    navigationPath: "/register",
    precondition: "registration form should be visible",
    action: "fill all fields except one required field and submit",
    testData: "valid name, email, password with one missing mandatory field",
    assertion: "existing entered values should remain and only missing field should be flagged",
    negativeCheck: "verify form does not reset unexpectedly on validation error",
    problemStatement: "Users abandon signup when validation wipes all previously entered values.",
    locators: ["form", "input", ".validation-text"],
  }),
];

const catalogScenarios = [
  ["catalog-01", "Verify storefront loads all main product cards", "/product", "storefront product cards", "product grid should load with image, title, and price", "verify at least one product card has valid image and detail link"],
  ["catalog-02", "Verify flash sale cards navigate to correct detail page", "/flash-sales", "flash sale navigation", "clicked card should open matching product detail page", "verify title and price match source card after navigation"],
  ["catalog-03", "Verify category page renders stable listing layout", "/product_phone_tablets", "category listing layout", "category page should display consistent cards and no broken layout", "verify responsive layout does not hide action links"],
  ["catalog-04", "Verify product detail page shows image, title, price, and specs", "/product-details/1", "product details content", "product detail page should show all major content blocks", "verify details page does not render empty values"],
  ["catalog-05", "Verify filter behavior keeps product count stable", "/product_phone_tablets", "filters or selection controls", "filtered results should update deterministically", "verify clearing filter restores original listing state"],
  ["catalog-06", "Verify sorting updates card order correctly", "/product_phone_tablets", "sorting controls", "selected sorting should reorder visible products", "verify sorting does not duplicate or remove cards unexpectedly"],
  ["catalog-07", "Verify search returns relevant products", "/product", "search field", "search result cards should match entered keyword intent", "verify zero-result search shows graceful empty state"],
  ["catalog-08", "Verify broken image fallback is not shown for valid products", "/product", "image rendering", "normal products should display actual image not broken placeholder", "verify image alt or fallback appears only when genuinely required"],
  ["catalog-09", "Verify breadcrumb or back navigation from detail page", "/product-details/1", "navigation recovery", "user should return to previous listing without losing context", "verify browser back does not land on blank or mismatched page"],
  ["catalog-10", "Verify pagination or long-list browsing is stable", "/product_phone_tablets", "long list interactions", "scroll or paging should keep products loadable and interactive", "verify no duplicate repeated cards appear on long browse"],
  ["catalog-11", "Verify category switch keeps previous search context isolated", "/product", "category switch after search", "switching categories should not leak old search state into unrelated listing", "verify previous keyword chip or count is cleared when category changes"],
  ["catalog-12", "Verify price badge and discount label remain aligned on product cards", "/product", "pricing presentation", "price, strike price, and offer label should render without overlap", "verify long product names do not break pricing layout"],
  ["catalog-13", "Verify recommended products rail opens correct detail page", "/home", "recommended items discovery", "recommended item click should land on matching product detail page", "verify recommended section does not route to wrong SKU"],
  ["catalog-14", "Verify recently viewed products update after opening multiple detail pages", "/product-details/1", "recently viewed memory", "recently viewed module should show latest visited products in order", "verify refresh does not duplicate the same recently viewed card"],
  ["catalog-15", "Verify brand section filters only matching brand products", "/product", "brand filtering", "brand selection should show products only from chosen brand family", "verify switching brand does not retain hidden products from old brand"],
  ["catalog-16", "Verify subcategory landing page keeps correct banner and heading", "/product_fashion", "subcategory page identity", "subcategory title, hero, and breadcrumb should match selected collection", "verify wrong category hero is not reused from previous route"],
  ["catalog-17", "Verify product highlights expand without layout shift", "/product-details/1", "product highlights accordion", "expanding highlights or specs should reveal content without clipping", "verify accordion collapse does not leave blank space artifact"],
  ["catalog-18", "Verify search with mixed-case keyword still returns relevant cards", "/product-search", "case-insensitive search", "search results should match product intent regardless of input casing", "verify whitespace-padded keywords do not break result rendering"],
  ["catalog-19", "Verify out-of-stock or unavailable messaging is visible on detail page", "/product-details/1", "availability messaging", "availability state should be clearly communicated near purchase CTA", "verify unavailable product still prevents misleading purchase state"],
  ["catalog-20", "Verify cross-category navigation from home merchandising banners", "/home", "home merchandising navigation", "home banner click should route to intended listing or promoted collection", "verify banner route does not open unrelated category or broken page"],
];

const catalogScenarioObjects = catalogScenarios.map(([id, title, navigationPath, feature, assertion, negativeCheck], index) =>
  buildScenario({
    id,
    suite: "Catalog and Discovery",
    title,
    difficulty: index < 3 ? "Beginner" : index < 7 ? "Intermediate" : "Advanced",
    type: "UI Functional",
    feature,
    homePath: "/home",
    navigationPath,
    precondition: "catalog data should be available and page should finish initial render",
    action: `open ${navigationPath} and interact with ${feature}`,
    testData: "search term, filter option, sort option, or selected product card",
    assertion,
    negativeCheck,
    problemStatement: "Catalog regressions usually hit revenue first because users cannot discover or trust products correctly.",
    locators: [".product-card", ".product-grid", ".product-image", ".product-title"],
  })
);

const cartCheckoutScenarios = [
  ["checkout-01", "Add one product to cart from listing page", "/product", "add to cart", "selected product should appear in cart summary", "verify repeated click does not create silent duplicates"],
  ["checkout-02", "Add product to cart from detail page", "/product-details/1", "detail add to cart", "cart should reflect product chosen from detail page", "verify selected quantity is honored"],
  ["checkout-03", "Update quantity in cart and verify total", "/product", "cart quantity update", "summary total should update based on quantity change", "verify total is not stale after quantity decrease"],
  ["checkout-04", "Remove product from cart", "/product", "cart removal", "removed product should disappear and total should reduce", "verify removed row does not return after refresh"],
  ["checkout-05", "Proceed with address selection", "/product", "address step", "selected address should persist until payment", "verify address mismatch does not reach payment state"],
  ["checkout-06", "Complete payment with valid simulated method", "/product", "payment submission", "successful payment should show order success state", "verify payment action is not double-submitted"],
  ["checkout-07", "Verify invoice generated once after payment", "/product", "invoice generation", "one payment should generate one invoice only", "verify duplicate invoice is not created after refresh"],
  ["checkout-08", "Verify order history reflects new purchase", "/orders-history", "order persistence", "latest order should appear in order history correctly", "verify amount and product title match purchase"],
  ["checkout-09", "Payment interruption should not create false success order", "/product", "failed payment path", "failed or cancelled payment should not create completed order", "verify no success banner or invoice appears"],
  ["checkout-10", "Verify post-order navigation and summary integrity", "/orders-success", "success flow review", "success page should show correct completion state and summary", "verify refresh does not create another order"],
  ["checkout-11", "Verify add to cart button changes to go to cart after item addition", "/product", "post-add cart CTA", "after adding item the CTA should let user navigate directly to cart", "verify CTA does not stay stuck in loading or old state"],
  ["checkout-12", "Verify toast appears instead of browser alert on cart add", "/product", "cart toast feedback", "cart addition should show non-blocking toast with product confirmation", "verify alert dialog never interrupts automation flow"],
  ["checkout-13", "Verify guest checkout route redirects user to address or login gate correctly", "/add-to-cart", "guest checkout gating", "guest should be guided to valid next step without broken checkout state", "verify proceed button does not bypass required identity step"],
  ["checkout-14", "Verify saved address selection updates checkout summary card", "/address", "saved address selection", "selected address should immediately reflect in shipping summary", "verify stale previous address is not used for final order"],
  ["checkout-15", "Verify new address form validation before moving to payment", "/address", "new address validation", "mandatory address fields should block progression when incomplete", "verify partial form does not create broken address record"],
  ["checkout-16", "Verify coupon or promo code adjusts payable total correctly", "/payment", "promotion application", "valid promotion should reduce final amount and show discount line item", "verify invalid code does not silently change totals"],
  ["checkout-17", "Verify cash on delivery selection preserves order summary", "/payment", "cash on delivery flow", "COD selection should keep totals, shipping, and confirmation details accurate", "verify COD option does not hide payable breakdown unexpectedly"],
  ["checkout-18", "Verify track order page shows correct milestone progression", "/track-order/INV-1001", "order tracking status", "tracking timeline should reflect current shipment stage in correct order", "verify future milestones are not incorrectly marked complete"],
  ["checkout-19", "Verify reorder or buy-again path from order history preloads cart correctly", "/orders-history", "reorder journey", "buy-again action should restore the right products into cart", "verify wrong quantity or unrelated SKU is not added"],
  ["checkout-20", "Verify invoice page shows tax, billing, shipping, and payment details", "/invoice/INV-1001", "invoice details validation", "invoice should contain complete financial and customer metadata", "verify printable invoice does not miss address or transaction reference"],
];

const cartCheckoutScenarioObjects = cartCheckoutScenarios.map(([id, title, navigationPath, feature, assertion, negativeCheck], index) =>
  buildScenario({
    id,
    suite: "Cart and Checkout",
    title,
    difficulty: index < 2 ? "Intermediate" : "Advanced",
    type: "Revenue E2E",
    feature,
    homePath: "/home",
    navigationPath,
    precondition: "at least one purchasable product should be available",
    action: `execute ${feature} flow from ${navigationPath}`,
    testData: "selected product, quantity, address data, payment method",
    assertion,
    negativeCheck,
    problemStatement: "Checkout defects are high severity because they directly impact revenue, trust, and order accuracy.",
    locators: [".product-card", ".cart-summary", "input[name='payment-method']", ".invoice-box"],
  })
);

const apiSecurityScenarios = [
  ["api-01", "Verify product API returns stable required fields", "Products API", "Schema and status validation"],
  ["api-02", "Verify empty search API response is handled correctly", "Search API", "Zero-result contract validation"],
  ["api-03", "Verify create account API blocks duplicate email", "Auth API", "Duplicate user prevention"],
  ["api-04", "Verify login API returns secure error on invalid password", "Auth API", "Auth failure contract"],
  ["api-05", "Verify unauthorized user cannot access protected order API", "Orders API", "Authorization coverage"],
  ["api-06", "Verify cart total API matches UI summary", "Cart API", "Data consistency validation"],
  ["api-07", "Verify payment callback does not create duplicate order records", "Payment API", "Idempotency validation"],
  ["api-08", "Verify invoice API returns correct order mapping", "Invoice API", "Invoice linkage validation"],
  ["api-09", "Verify invalid payload returns meaningful 4xx response", "Validation API", "Negative request handling"],
  ["api-10", "Verify role-based dashboard API blocks student token for admin data", "Role API", "Security defect prevention"],
];

const apiScenarioObjects = apiSecurityScenarios.map(([id, title, feature, problemStatement], index) =>
  buildScenario({
    id,
    suite: "API and Security",
    title,
    difficulty: "Advanced",
    type: "API Contract",
    feature,
    homePath: "/api-testing",
    navigationPath: "/api-testing",
    precondition: "API endpoint, auth token, and request payload should be ready",
    action: `send request against ${feature} and validate response behavior`,
    testData: "request payload, auth token, expected schema, negative input set",
    assertion: "status code, response body, and business contract should be correct",
    negativeCheck: "invalid or unauthorized requests should fail safely with deterministic response",
    problemStatement,
    locators: ["endpoint url", "request body", "response schema", "auth header"],
  })
);

const bugRegressionScenarios = [
  ["bug-01", "Raise bug for broken product image on category page", "/bugs/create"],
  ["bug-02", "Raise bug for stale cart total after quantity update", "/bugs/create"],
  ["bug-03", "Raise bug for duplicate order after double payment click", "/bugs/create"],
  ["bug-04", "Raise bug for missing order history after successful payment", "/bugs/create"],
  ["bug-05", "Raise bug for invalid duplicate email handling", "/bugs/create"],
  ["bug-06", "Raise bug for student access to admin route", "/bugs/create"],
  ["bug-07", "Verify bug detail page captures evidence clearly", "/bugs/1"],
  ["bug-08", "Verify reopen flow when defect is not fully fixed", "/bugs"],
  ["bug-09", "Verify assigned bug filtering for trainer or admin", "/assigned-bugs"],
  ["bug-10", "Verify my bugs view shows reporter-relevant defects", "/my-bugs"],
];

const bugScenarioObjects = bugRegressionScenarios.map(([id, title, navigationPath], index) =>
  buildScenario({
    id,
    suite: "Bug Reporting and Regression",
    title,
    difficulty: index < 4 ? "Intermediate" : "Advanced",
    type: "Process + UI",
    feature: "JIRA-style bug workflow",
    homePath: "/home",
    navigationPath,
    precondition: "bug tracker access and reproducible issue details should exist",
    action: `open ${navigationPath} and complete reporting or tracking activity`,
    testData: "summary, steps to reproduce, actual result, expected result, severity, priority, evidence",
    assertion: "bug should be logged or visible with correct ownership and traceability",
    negativeCheck: "verify incomplete report does not create low-quality ticket silently",
    problemStatement: "A strong automation learner should connect observed issue with reporting discipline, not only UI clicking.",
    locators: ["input[name='summary']", "textarea", ".bug-status", ".bug-card"],
  })
);

const frameworkScenarios = [
  ["framework-01", "Design page object for login and register flow"],
  ["framework-02", "Design reusable wait helper for dynamic buttons"],
  ["framework-03", "Design test data layer for users and products"],
  ["framework-04", "Design assertion utility for cart and invoice validation"],
  ["framework-05", "Design API client wrapper for auth and order calls"],
  ["framework-06", "Design retry-safe payment submission helper"],
  ["framework-07", "Design reporting layer with screenshot and console capture"],
  ["framework-08", "Design environment config for dev, qa, and production-like urls"],
  ["framework-09", "Design tag-based suite execution for smoke and regression"],
  ["framework-10", "Design CI-friendly execution strategy for UI and API tests"],
];

const frameworkScenarioObjects = frameworkScenarios.map(([id, title]) =>
  buildScenario({
    id,
    suite: "Framework Design",
    title,
    difficulty: "Advanced",
    type: "Framework Thinking",
    feature: "Automation framework design",
    homePath: "/automation-testing",
    navigationPath: "/automation-testing",
    precondition: "student should understand the related module and target business flow",
    action: "convert repeated automation actions into reusable framework components",
    testData: "class names, locators, test data files, execution tags, reporting hooks",
    assertion: "framework design should reduce duplication and improve maintainability",
    negativeCheck: "verify design does not tightly couple tests to fragile page structure",
    problemStatement: "Users should be able to build their own framework from your website, not just run isolated scripts.",
    locators: ["page object methods", "fixtures", "hooks", "report utilities"],
  })
);

export const automationScenarioLibrary = [
  ...automationExerciseDetailedCases,
  ...authScenarios,
  ...catalogScenarioObjects,
  ...cartCheckoutScenarioObjects,
  ...apiScenarioObjects,
  ...bugScenarioObjects,
  ...frameworkScenarioObjects,
];

export const automationScenarioSuites = [
  { id: "all", label: "All Scenarios", count: automationScenarioLibrary.length },
  { id: "Automation Exercise Core", label: "Automation Exercise Core", count: automationExerciseDetailedCases.length },
  { id: "Authentication", label: "Authentication", count: authScenarios.length },
  { id: "Catalog and Discovery", label: "Catalog and Discovery", count: catalogScenarioObjects.length },
  { id: "Cart and Checkout", label: "Cart and Checkout", count: cartCheckoutScenarioObjects.length },
  { id: "API and Security", label: "API and Security", count: apiScenarioObjects.length },
  { id: "Bug Reporting and Regression", label: "Bug Reporting and Regression", count: bugScenarioObjects.length },
  { id: "Framework Design", label: "Framework Design", count: frameworkScenarioObjects.length },
];

export const automationFrameworkBlueprint = [
  "tests/ui/auth",
  "tests/ui/catalog",
  "tests/ui/checkout",
  "tests/api",
  "pages/LoginPage",
  "pages/ProductCatalogPage",
  "pages/CheckoutPage",
  "fixtures/users.json",
  "fixtures/products.json",
  "utils/waits",
  "utils/reporting",
  "config/environments",
];
